<!-- eslint-disable vue/no-v-text-v-html-on-component -->
<template>
  <div class="base-form-container">
    <form
      v-if="ready && formAction"
      ref="form"
      :class="{ 'base-form': true, 'base-form--mobile': isMobile, 'grid-layout': !flex }"
      :style="styles"
      :method="method"
      :action="action"
      @submit.prevent="submit()"
    >
      <input
        v-if="hasToken"
        type="hidden"
        name="_token"
        :value="csrf"
      >
      <template
        v-for="(item, index) in formData"
      >
        <component
          :is="item.component"
          v-if="isDefaultHTMLElement(item.component)"
          v-show="showField[getFieldName(item, index)]"
          :key="`default-html-element-${index}`"
          :ref="getFieldName(item, index)"
          :style="getItemStyle(item)"
          v-bind="item.data"
          v-html="item.html"
        />
        <component
          :is="item.component"
          v-else-if="typeof form[item.data.name] !== 'undefined'"
          v-show="showField[item.data.name]"
          :key="item.data.name"
          :ref="item.data.name"
          v-bind="item.data"
          v-model="form[item.data.name].value"
          :defaults="defaultObject[item.data.name] instanceof Object
            ? defaultObject[item.data.name]
            : null"
          :style="getItemStyle(item)"
          :error="form[item.data.name].error"
          :prefix="getItemName(item, 'prefix') ? form[getItemName(item, 'prefix')].value : null"
          :unit="getItemName(item, 'unit') ? form[getItemName(item, 'unit')].value : null"
          @clear="clear($event)"
          @update="[emitComponent(item, $event), clearErrors()]"
          @updatePrefix="form[getItemName(item, 'prefix')].value = $event"
          @updateUnit="form[getItemName(item, 'unit')].value = $event"
        />
      </template>
    </form>
    <div
      v-else-if="ready && !formAction"
      ref="form"
      :class="{ 'base-form': true, 'base-form--mobile': isMobile, 'grid-layout': !flex }"
      :style="styles"
      @submit.prevent="submit()"
    >
      <template
        v-for="(item, index) in formData"
      >
        <component
          :is="item.component"
          v-if="isDefaultHTMLElement(item.component)"
          v-show="showField[getFieldName(item, index)]"
          :key="`default-html-element-${index}`"
          :ref="getFieldName(item, index)"
          :style="getItemStyle(item)"
          v-bind="item.data"
          v-html="item.html"
        />
        <component
          :is="item.component"
          v-else-if="typeof form[item.data.name] !== 'undefined'"
          v-show="showField[item.data.name]"
          :key="item.data.name"
          :ref="item.data.name"
          v-bind="item.data"
          v-model="form[item.data.name].value"
          :defaults="defaultObject[item.data.name] instanceof Object
            ? defaultObject[item.data.name]
            : null"
          :style="getItemStyle(item)"
          :error="form[item.data.name].error"
          :prefix="getItemName(item, 'prefix') ? form[getItemName(item, 'prefix')].value : null"
          :unit="getItemName(item, 'unit') ? form[getItemName(item, 'unit')].value : null"
          @clear="clear($event)"
          @updatePrefix="form[getItemName(item, 'prefix')].value = $event"
          @updateUnit="form[getItemName(item, 'unit')].value = $event"
        />
      </template>
    </div>
    <slot
      :config="config"
      :form="form"
      :submit="submit"
    />
    <LoadingOverlay :show="showLoader && isLoading" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import IoMediaQueryMixin from '../../../shared/mixins/IoMediaQueryMixin';
import BaseFormMixin from './mixins/BaseFormMixin';
import LoadingOverlay from '../../../common/LoadingOverlay.vue';

import IoAlerts from '../../IoAlerts.vue';

/* eslint-disable no-undef */
export default {
  name: 'BaseForm',

  components: {
    IoAlerts,
    LoadingOverlay,
  },

  mixins: [
    IoMediaQueryMixin,
    BaseFormMixin,
  ],

  props: {
    action: {
      type: String,
      default: '',
    },
    deleteAction: {
      type: String,
      default: '',
    },
    method: {
      type: String,
      default: 'post',
      validate(val) {
        return ['post', 'get', 'put', 'patch'].indexOf(_.lowerCase(val)) !== -1 ? _.lowerCase(val) : false;
      },
    },
    data: {
      type: Array,
      default: () => ([]),
    },
    formDefaults: {
      type: Object,
      default: null,
    },
    name: {
      type: String,
      default: 'base-form',
    },
    layout: {
      type: String,
      default: '',
    },
    alignItems: {
      type: String,
      default: 'baseline',
    },
    flex: {
      type: Boolean,
      default: false,
    },
    delay: {
      type: Number,
      default: 0,
    },
    breakpoint: {
      type: Number,
      default: 860,
    },
    token: {
      type: Boolean,
      default: false,
    },
    empty: {
      type: Boolean,
      default: false,
    },
    contentType: {
      type: String,
      default: 'formData',
      validator(val) {
        return ['formData', 'json'].indexOf(val) !== -1;
      },
    },
    showLoader: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      formRef: null,
      currentForm: {},
      csrf: document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
      formToken: this.token,
      formAction: this.action,
      formMethod: this.method,
      formRequestFormatter: this.requestFormatter,
      formAlignment: this.alignItems,
      form: {},
      deleteChain: [],
      isMounted: false,
      defaultObject: null,
      isMobile: false,
      config: null,
      formData: [],
      showField: {},
      defaultHTMLElements: [
        'div',
        'p',
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
      ],
    };
  },

  computed: {
    styles: {
      get() {
        return {
          '--grid-template-columns': this.formLayout,
          '--align-items': this.alignment,
        };
      },
    },
    ...mapGetters('baseForm', {
      get_defaults: 'GET_DEFAULTS',
    }),
    defaults() {
      return this.formDefaults || this.get_defaults;
    },
    formDataInputs() {
      return this.formData.filter((item) => !this.isDefaultHTMLElement(item.component));
    },
    ready() {
      // If empty prop is true, always render
      if (this.empty) return true;
      // If we have any formData (including HTML elements), consider it ready
      if (this.formData.length > 0) return true;
      // Otherwise check if form has content
      return !this.IOH.Object.isEmpty(this.form);
    },
    formLayout() {
      let cols = 0;

      if (!this.layout) {
        this.data.forEach((field) => {
          const count = field.col + field.span - 1;
          if (count > cols) {
            cols = count;
          }
        });

        return `repeat(${cols}, 1fr)`;
      }
      return this.layout;
    },
    hasToken: {
      get() {
        return this.formToken;
      },
      set(val) {
        this.formToken = val;
      },
    },
    alignment: {
      get() {
        return this.formAlignment;
      },
      set(val) {
        this.formAlignment = val;
      },
    },
  },
  watch: {
    data() {
      this.init();
    },
    defaults() {
      this.init();
    },
    form: {
      deep: true,
      handler() {
        this.clearErrors();
        if (!Object.keys(this.formData).length) {
          return false;
        }

        this.$nextTick(() => {
          const visible = {};
          this.formData.forEach((field, index) => {
            visible[this.getFieldName(field, index)] = this.resolve(field.if);
          });
          this.showField = visible;
        });

        return true;
      },
    },
    showField: {
      deep: true,
      handler(val, prev) {
        Object.entries(val).forEach(([key, value]) => {
          if (value !== prev[key] && this.form[key] && this.$refs[key] && this.$refs[key][0]) {
            this.form[key].value = this.$refs[key][0].value;
          }
        });
      },
    },
  },
  mounted() {
    this.init();

    GlobalEvents.listen(`${this.name}.submit`, () => this.submit());
    GlobalEvents.listen(`${this.name}.update`, () => this.forceUpdateComponent());
    GlobalEvents.listen(`${this.name}.submit.form`, () => {
      this.formRef.submit();
    });
    // eslint-disable-next-line no-return-assign
    GlobalEvents.listen(`${this.name}.clear`, () => this.form = {});
  },
  destroyed() {
    GlobalEvents.destroy(`${this.name}.submit`);
    GlobalEvents.destroy(`${this.name}.update`);
    GlobalEvents.destroy(`${this.name}.submit.form`);
    GlobalEvents.destroy(`${this.name}.clear`);
  },

  updated() {
    if ('default' in this.mediaQuery) {
      this.isMobile = !this.mediaQuery.default.match;
    }
  },
  methods: {
    init() {
      this.formRef = this.$refs.form;

      setTimeout(() => {
        this.form = {};
        const form = {};
        this.formData = _.cloneDeep(this.data);

        this.setFormHandling();

        this.setBreakpoint();

        this.formDataInputs.forEach((item) => {
          let { value } = item.data;

          const loopVal = (obj, path) => Object.entries(obj).forEach(([key, val]) => {
            if (val instanceof Array) {
              let currPath = null;
              if (path !== null) {
                currPath = path !== '' ? `${path}.${key}` : key;
              } else {
                currPath = key;
              }

              if (item.data.name === currPath) {
                value = val;
              }
            } else if (val instanceof Object && typeof val.value === 'undefined') {
              let currPath = null;
              if (path !== null) {
                currPath = path !== '' ? `${path}.${key}` : key;
              } else {
                currPath = '';
              }

              loopVal(val, currPath);
            } else {
              let currPath = null;
              if (path !== null) {
                currPath = path !== '' ? `${path}.${key}` : key;
              } else {
                currPath = key;
              }

              if (item.data.name === currPath && val !== null) {
                value = typeof val.value === 'undefined' ? val : val.value;
              }
            }
          });

          loopVal(this.defaultObject, '');

          if (!form[item.data.name]) {
            form[item.data.name] = {};
          }

          this.mergeErrors();

          if (typeof value !== 'undefined') {
            form[item.data.name].value = value;
          } else if (typeof item.data.value !== 'undefined') {
            form[item.data.name].value = item.data.value;
          } else {
            form[item.data.name].value = '';
          }

          if (item.models?.length) {
            item.models.forEach((model) => {
              form[model.key] = {};
              form[model.key].value = this.defaultObject[model.key] || item.data[model.prop];
            });
          }

          ['prefix', 'unit'].forEach((name) => {
            const prefixKey = this.getItemName(item, name);

            if (prefixKey) {
              form[prefixKey] = {
                value: this.defaultObject[prefixKey] || item.data[name],
              };
            }
          });
        });

        this.form = _.cloneDeep(_.merge(this.form, form));

        // Initialize showField for all fields including HTML elements
        const visible = {};
        this.formData.forEach((field, index) => {
          visible[this.getFieldName(field, index)] = this.resolve(field.if);
        });
        this.showField = visible;

        if (this.formData.length) {
          this.isMounted = true;
          this.$emit('mounted', true);
        } else {
          this.isMounted = false;
          this.$emit('mounted', false);
        }
      }, this.delay);
    },
    getItemName(item, type) {
      if (type) {
        return item.data[`${type}Key`]
            || (
              item.data[type === 'prefix' ? 'prefixes' : 'units']
                ? `${item.data.name}${_.upperFirst(type)}`
                : null
            );
      }

      return item.data.name;
    },
    modelHandler(component, models) {
      models.forEach((model) => {
        component.$on(model.event, (value) => {
          this.form[model.key] = {};
          this.form[model.key].value = value;
        });
      });
    },
    emitComponent({ data }, value) {
      this.$emit(`update:${data.name}`, { value, values: this.form });
    },
    mergeErrors() {
      this.formDataInputs.forEach((item) => {
        if (this.defaults?.[item.data.name]?.error) {
          this.form[item.data.name].error = this.defaults?.[item.data.name]?.error;
        }
      });
    },
    setFormHandling() {
      this.formAction = this.action;
      this.formMethod = this.method;
      this.defaultObject = typeof this.defaults === 'string' ? JSON.parse(this.defaults) : this.defaults;

      if (this.defaultObject.action) {
        this.formAction = this.defaultObject.action;
      }
      if (this.defaultObject.method) {
        this.formMethod = this.defaultObject.method;
      }
      if (this.defaultObject.formatter) {
        this.formRequestFormatter = this.defaultObject.formatter;
      }
      if (this.defaultObject.token) {
        this.hasToken = this.defaultObject.token;
      }
      if (this.defaultObject.alignment) {
        this.alignment = this.defaultObject.alignment;
      }
    },
    setBreakpoint() {
      this.config = null;

      if (this.formData.length && typeof this.formData[0].component === 'undefined') {
        // eslint-disable-next-line prefer-destructuring
        this.config = this.formData[0];
        this.formData.splice(0, 1);
      }

      const bp = this.config && typeof this.config.breakpoint !== 'undefined'
        ? this.config.breakpoint
        : this.breakpoint;

      // eslint-disable-next-line no-underscore-dangle
      this.isMobile = !this._media(bp, true, 'min');
    },
    resolve(cond = []) {
      let condition = cond;

      if (typeof condition === 'boolean') {
        return condition;
      }
      if (condition instanceof Array && !condition.length) {
        return true;
      }

      const internalVars = Object.keys(this.form);

      condition = condition instanceof Array
        ? condition.map((x) => `(${x})`).join(' && ')
        : condition;

      const normalizedCond = condition.toString().replace(/('.*?')/, '');
      let isValid = false;

      internalVars.forEach((variable) => {
        if (normalizedCond.includes(variable)) {
          condition = condition.replaceAll(new RegExp(`\\b${variable}\\b`, 'gi'), `this.form?.['${variable}']?.value`);
          isValid = true;
        }
      });

      if (!isValid) {
        return true;
      }

      // eslint-disable-next-line no-eval
      return eval(condition);
    },
    submit() {
      if (typeof this.formAction === 'undefined') {
        this.forceUpdateComponent();
        this.$emit('update', this.form);
      } else {
        const responses = {};

        if (this.deleteChain.length && this.delete) {
          this.deleteChain.forEach((name) => {
            this.delete(name).then(() => {
              responses[name] = true;
              let noErrors = true;

              // eslint-disable-next-line no-return-assign
              Object.values(responses).forEach((x) => (x ? '' : noErrors = false));

              if (noErrors) {
                this.send(
                  this.form,
                  this.formAction,
                  this.formMethod,
                  true,
                  this.hasToken
                    ? this.csrf
                    : false,
                  this.contentType,
                );
              }
            }).catch(() => {
              responses[name] = false;
            });
          });
        } else {
          this.send(
            this.form,
            this.formAction,
            this.formMethod,
            true,
            this.hasToken
              ? this.csrf
              : false,
            this.contentType,
          );
        }
      }
    },
    clear(e) {
      this.clearErrors();
      const chainIndex = this.deleteChain.indexOf(e.name);

      switch (e.action) {
        case 'replace':
          if (chainIndex !== -1) {
            this.deleteChain.splice(chainIndex, 1);
          }
          break;
        case 'clear':
          if (chainIndex === -1) {
            this.deleteChain.push(e.name);
            this.form[e.name].value = '';
          }
          break;
        default:
          break;
      }
    },
    clearErrors() {
      Object.values(this.form).forEach((input) => {
        // eslint-disable-next-line no-param-reassign
        input.error = '';
      });
      this.$emit('update', this.form);
    },
    delete(name) {
      const params = { name, _method: 'DELETE' };

      return new Promise((resolve, reject) => {
        axios.post(this.deleteAction, params)
          .then((res) => {
            this.deleteChain.pop(name);
            this.$emit('deleted', {
              response: res,
              fields: {
                [name]: this.$refs[name] && this.$refs[name][0] ? this.$refs[name][0] : null,
              },
            });
            resolve(res);
          })
          .catch((err) => {
            GlobalEvents.fire('alert', [{ message: err.response.data.message, type: 'error' }]);
            reject(err);
          });
      });
    },
    getFieldName(item, index) {
      return item.data?.name || `noname-${index}`;
    },
    getItemStyle(item) {
      return {
        ...(item?.style || {}),
        'grid-column': `${item.col ? item.col : 1}/${item.span ? item.span + item.col : -1}`,
      };
    },
    isDefaultHTMLElement(componentName) {
      return this.defaultHTMLElements.includes(componentName.toLowerCase());
    },
  },
};
/* eslint-disable no-undef */
</script>

<style lang="scss" scoped>
.base-form {
    display: flex;
    flex-wrap: wrap;
    align-items: var(--align-items);
    grid-column-gap: 1rem;
    grid-row-gap: 1rem;

    &.grid-layout {
        display: flex;
        flex-direction: column;
        grid-row-gap: 1rem;

        &:not(.base-form--mobile) {
            display: grid;
            grid-template-columns: var(--grid-template-columns);
            grid-column-gap: 1rem;
        }
    }
}
</style>
